import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";

import island1 from "../assets/island1-min.webp";
import island2 from "../assets/island2-min.webp";
import island3 from "../assets/island3-min.webp";
import island4 from "../assets/island4-min.webp";
import island5 from "../assets/island5-min.webp";

import mainland1 from "../assets/mainland1-min.webp";
import mainland2 from "../assets/mainland2-min.webp";
import mainland3 from "../assets/mainland3-min.webp";
import mainland4 from "../assets/mainland4-min.webp";
import mainland5 from "../assets/mainland5-min.webp";
const privateEventsImage = "/assets/private-events.webp";

function PrivateEventsPage() {
  const islandImages = [island1, island2, island3, island4, island5];
  const mainlandImages = [mainland1, mainland2, mainland3, mainland4, mainland5];

  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % islandImages.length);
        setFade(true);
      }, 500);
    }, 3000);
    return () => clearInterval(interval);
  }, [islandImages.length]);

  const termsItems = [
    { title: "Booking Policy", text: "Bookings are subject to availability. A 50% deposit is required to secure your reservation and the balance must be paid two weeks before the event date. For bookings made less than two weeks before the event, full payment is required." },
    { title: "Use of Facilities", text: "Events must finish by 11:30pm, with cleaning allowed until midnight. Weekend hours may be extended by prior agreement. The hall may only be used for the purpose stated in this application." },
    { title: "Noise", text: "The applicant is responsible for ensuring that noise levels remain within regulatory limits. Music must stop by 11:30pm on weekdays; weekend hours may be extended by agreement." },
    { title: "Liability", text: "The applicant is responsible for any damage to the hall, its equipment, or facilities during the rental period. A refundable security deposit (£100–£200) is required depending on the event type." },
    { title: "Fees", text: "The hall rental fee is £700 per day or £100 per hour. Music equipment can be hired at £200 per event." },
    { title: "Cleanliness", text: "The hall and car park must be left in the same condition as found. Additional cleaning charges may apply if this condition is not met." },
    { title: "Cancellations", text: "Notice of cancellation must be given at least 21 days before the event to receive a refund. Cancellations within 14 days may be subject to partial or no refund." },
    { title: "Alcohol and Smoking", text: "Alcohol may only be served with appropriate licensing. Smoking is strictly prohibited within the hall premises." },
    { title: "Decorations", text: "Decorations must be approved before use. Nails, staples, adhesives, confetti, and live candles are not permitted. Decorations cannot be done the day before and all items must be removed on the same day." },
    { title: "Security", text: "Security personnel must be present to control the car park and maintain order. The cost will be charged to the client or they may provide their own security." },
    { title: "Children", text: "Children must be supervised at all times. Keep them within the designated hall as rented. They must not play near the main road unsupervised." },
    { title: "Privacy", text: "Your personal data will be used solely for processing your hall rental application and managing your booking. Your information will not be shared with third parties." },
  ];

  return (
    <div className="text-white text-center font-forum page-container">
      <Helmet>
        <title>Private Events | LasGidi Restaurant</title>
        <meta name="description" content="Book LasGidi Restaurant's private event halls for birthdays, weddings, corporate dinners, and celebrations. Premium space, catering, and ambience." />
        <meta name="keywords" content="LasGidi Restaurant Event Hall, Manchester Party Venue, Nigerian Restaurant Events, Hall Booking Manchester, Private Event Space" />
        <meta property="og:title" content="Private Events | LasGidi Restaurant" />
        <meta property="og:description" content="Host your next event at LasGidi Restaurant. Perfect for birthdays, weddings, corporate parties, and private dining experiences." />
        <meta property="og:type" content="website" />
        <link rel="preload" as="image" href={privateEventsImage} fetchpriority="high" />
      </Helmet>

      {/* Hero banner */}
      <div className="relative w-full h-[200px] mb-10 flex items-center justify-center overflow-hidden">
        <img 
          src={privateEventsImage} 
          alt="Host Your Events" 
          className="absolute inset-0 w-full h-full object-cover"
          fetchpriority="high"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/50" />
        <h1 className="relative z-10 text-white text-4xl font-bold font-prata">Host Your Events</h1>
      </div>

      {/* Intro */}
      <section className="max-w-[600px] mx-auto mb-[50px] text-white px-10">
        <p className="text-xl leading-relaxed mb-[30px] text-justify">
          Make your celebrations unforgettable with Lasgidi Restaurant. From corporate gatherings to intimate parties or a grand celebration, we have the ideal setting for you. Let us make your event unforgettable.
          <br /><br />
          To secure a booking, please review our terms below and contact our events team directly.
        </p>
      </section>

      {/* Halls */}
      <section>
        <h2 className="text-center text-2xl mb-5 text-brand-orange font-prata">- Our Halls -</h2>
        <div className="flex justify-between w-full h-[60vh] gap-2.5 mx-auto mb-[60px] px-[110px] max-lg:px-[50px] max-[420px]:px-[30px] text-center max-lg:flex-col max-lg:gap-[30px]">
          {[
            { imgs: islandImages, label: "Island Hall" },
            { imgs: mainlandImages, label: "Mainland Hall" },
          ].map(({ imgs, label }, i) => (
            <div key={i} className={`relative w-[48%] rounded-[10px] transition-opacity duration-1000 ${fade ? "opacity-100" : "opacity-0"} max-lg:w-full max-lg:h-[400px] overflow-hidden`}>
              <img 
                src={imgs[index]} 
                alt={label} 
                className="w-full h-full object-cover transition-opacity duration-1000" 
                loading="lazy"
              />
              <div className="absolute bottom-5 left-2.5 bg-black/60 text-white p-[15px] rounded">
                <h2 className="text-base m-0 break-words font-prata">{label}</h2>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Terms */}
      <section className="py-[60px] px-[110px] max-lg:px-[50px] max-[420px]:px-[30px] bg-white/[0.03] mt-10 max-[768px]:py-10">
        <h2 className="text-center text-2xl mb-5 text-brand-orange font-prata">- Booking Information -</h2>
        <div className="max-w-[1100px] mx-auto text-center max-[768px]:text-left">
          <p className="text-[1.1rem] text-white mb-10 italic opacity-90">
            By proceeding to make a booking via the contact details below, you agree to the Terms and Conditions listed here.
          </p>
          <h3 className="text-2xl text-brand-orange mb-5 uppercase tracking-[2px]">Terms & Conditions</h3>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-[30px] mt-[30px] text-left max-[768px]:grid-cols-1">
            {termsItems.map(({ title, text }, i) => (
              <div key={i}>
                <h3 className="text-[1.2rem] text-brand-orange mb-2.5 border-b border-[rgba(250,147,36,0.2)] pb-[5px]">{title}</h3>
                <p className="text-[0.95rem] leading-[1.5] text-white/80">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="py-[60px] px-5 pb-[100px]" style={{ background: "linear-gradient(to bottom, transparent, rgba(0,0,0,0.2))" }}>
        <p className="text-[1.2rem] mb-5 text-white">To book the hall, please contact us:</p>
        <a
          className="inline-block mx-2.5 bg-brand-orange text-black font-bold py-[15px] px-10 rounded transition-[transform,opacity] duration-300 hover:scale-105 hover:opacity-90 no-underline"
          href="tel:07733808828"
        >
          Call for Bookings: 07733 808 828
        </a>
      </div>
    </div>
  );
}

export default PrivateEventsPage;
